<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import { usePipelinesStore } from 'dashboard/stores/pipelines';

const props = defineProps({ pipelineId: { type: [Number, String], default: null } });
const emit = defineEmits(['confirm']);
const store = usePipelinesStore();
const dialogRef = ref(null);
const reasons = ref([]);
const form = reactive({ lost_reason_id: null, observation: '' });

function open()  { dialogRef.value?.open(); fetchReasons(); }
function close() { dialogRef.value?.close(); }
defineExpose({ open, close });

async function fetchReasons() {
  if (!props.pipelineId) return;
  reasons.value = await store.listLostReasons(props.pipelineId);
}

watch(() => props.pipelineId, fetchReasons);
onMounted(fetchReasons);

// Opções dinâmicas a partir dos motivos cadastrados no pipeline.
const reasonOptions = computed(() => reasons.value.map(r => ({ value: r.id, label: r.name })));

function confirm() {
  if (!form.lost_reason_id) return;
  emit('confirm', { lost_reason_id: form.lost_reason_id, observation: form.observation });
  Object.assign(form, { lost_reason_id: null, observation: '' });
  close();
}
</script>

<template>
  <Dialog ref="dialogRef" width="md" :show-cancel-button="false" :show-confirm-button="false">
    <div class="flex flex-col gap-4">
      <div class="flex items-start gap-3">
        <span class="size-10 rounded-full bg-n-ruby-3 flex items-center justify-center flex-shrink-0">
          <i class="i-lucide-x-circle size-5 text-n-ruby-11" />
        </span>
        <div>
          <h3 class="text-base font-medium text-n-slate-12 mb-0.5">{{ $t('PIPELINES.MOVE_TO_LOST.TITLE') }}</h3>
          <p class="text-xs text-n-slate-11 mb-0">
            {{ $t('PIPELINES.MOVE_TO_LOST.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-3 mt-2">
        <div class="flex items-center gap-4">
          <label class="text-xs text-n-slate-11 min-w-[120px]">{{ $t('PIPELINES.MOVE_TO_LOST.REASON_LABEL') }}</label>
          <SelectInput
            v-model="form.lost_reason_id"
            :options="reasonOptions"
            :placeholder="$t('PIPELINES.MOVE_TO_LOST.REASON_PLACEHOLDER')"
            class="flex-1 [&>select]:w-full"
          />
        </div>

        <div class="flex items-start gap-4">
          <label class="text-xs text-n-slate-11 min-w-[120px] pt-2">{{ $t('PIPELINES.MOVE_TO_LOST.OBSERVATION_LABEL') }}</label>
          <textarea
            v-model="form.observation"
            rows="3"
            :placeholder="$t('PIPELINES.MOVE_TO_LOST.OBSERVATION_PLACEHOLDER')"
            class="flex-1 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:border-n-brand"
          />
        </div>
      </div>

      <div class="flex justify-end gap-2 pt-4 border-t border-n-weak -mx-6 px-6">
        <NextButton faded slate :label="$t('PIPELINES.MOVE_TO_LOST.CANCEL')" @click="close" />
        <NextButton ruby :disabled="!form.lost_reason_id" :label="$t('PIPELINES.MOVE_TO_LOST.CONFIRM')" @click="confirm" />
      </div>
    </div>
  </Dialog>
</template>
